/**
 * The teams this learner is in, and the invitations waiting for them.
 *
 * The state itself is not here any more. It was, and the reason it moved is
 * the reason this file used to give for keeping it: there was one reader, so a
 * hook was the smaller thing, and the day something outside the dock needed it
 * it would become a store. The learner's own profile screen lists their teams
 * now (components/profile/Profile.tsx), and it is a screen the dock is mounted
 * *over* — two independent copies would mean leaving a team in the panel and
 * watching the page behind it go on showing it. So `lib/teams.ts` holds one
 * state and this is a subscription to it plus the writes.
 *
 * ## When it asks
 *
 * On mount, on a change of account, when the panel opens, and after any write.
 * Not on a timer.
 *
 * An invitation is the one thing here that arrives without this tab doing
 * anything, so a poll is the obvious thing to reach for and is the wrong
 * trade: teams are answered in hours, not seconds, and a tab open all day
 * polling a D1-backed route to find out whether somebody has been asked to
 * join a study group is a request every few seconds spent on nothing. Somebody
 * looking is the moment worth spending a request on — the same argument
 * `refreshOccupancy` already makes for the room picker — and two readers
 * looking at once are coalesced into one request by the store.
 *
 * Signed out there is nothing to ask about and nothing is asked: every route
 * answers 404 without a session, and a learner with no account has no teams
 * in the same way they have no inbox.
 */
import { useCallback, useEffect, useSyncExternalStore } from 'react';
import {
  answerInvite as answerInviteRoute,
  createTeam as createTeamRoute,
  inviteToTeam as inviteRoute,
  leaveTeam as leaveRoute,
  onTeams,
  refreshTeams,
  teamsForAccount,
  teamsSnapshot,
  type TeamState,
  type Wrote,
} from '../lib/teams';

export interface TeamsApi extends TeamState {
  /** True while the first load for this session is still out. */
  loading: boolean;
  /** Ask again. Called when the panel opens and after every write. */
  refresh(): void;
  create(name: string): Promise<Wrote>;
  invite(teamId: string, tag: string): Promise<Wrote>;
  answer(inviteId: number, accept: boolean): Promise<Wrote>;
  leave(teamId: string): Promise<Wrote>;
}

export function useTeams(signedIn: boolean): TeamsApi {
  // The same snapshot function for the server render as for the client: there
  // is no server here, and a store that has been told nothing is empty in both
  // worlds anyway (state/useTheme.ts makes the same call for the same reason).
  const view = useSyncExternalStore(onTeams, teamsSnapshot, teamsSnapshot);

  // Both halves of this are the store's, and both are cheap to repeat: a
  // change of account empties it before asking again, and a second reader
  // mounting beside the first is coalesced into the request already out.
  useEffect(() => {
    teamsForAccount(signedIn);
  }, [signedIn]);

  const refresh = useCallback(() => {
    if (signedIn) void refreshTeams();
  }, [signedIn]);

  /**
   * Every write refreshes on success and does not on failure.
   *
   * A refused write changed nothing, so re-reading would be a request spent to
   * be told what the panel already shows — and the reason it was refused is in
   * the answer, which the caller is about to put on screen.
   */
  const after = useCallback(
    async (work: Promise<Wrote>): Promise<Wrote> => {
      const done = await work;
      if (done.ok) refresh();
      return done;
    },
    [refresh],
  );

  const create = useCallback((name: string) => after(createTeamRoute(name)), [after]);
  const invite = useCallback((teamId: string, tag: string) => after(inviteRoute(teamId, tag)), [after]);
  const answer = useCallback((id: number, accept: boolean) => after(answerInviteRoute(id, accept)), [after]);
  const leave = useCallback((teamId: string) => after(leaveRoute(teamId)), [after]);

  return { ...view, refresh, create, invite, answer, leave };
}
